import { union } from '@ngrx/store';

import { GlobalActions } from '@fcom/core/actions';
import { CartPartStatus, CartState, CartStatus, UpdatingCartPart } from '@fcom/dapi';
import { DataUtils, setIn } from '@fcom/core/utils';
import { FinnairCart } from '@fcom/dapi/api/models';
import { GlobalBookingActions } from '@fcom/common/store';
import { SeatMapActions, ServicesActions } from '@fcom/common-booking/store';

import { BookingActions, CartActions, OffersActions } from '../actions';

export const initialState: CartState = Object.seal({
  cartStatus: CartStatus.INITIAL,
  dirty: false,
  fareRules: {},
});

// Extra type safety to make sure assigned constants are valid keys
type CartStateKey<T> = T extends keyof CartState ? T : never;

const CART_STATUS: CartStateKey<'cartStatus'> = 'cartStatus';
const CART_URL: CartStateKey<'cartUrl'> = 'cartUrl';
const CART_DATA: CartStateKey<'cartData'> = 'cartData';
const IS_UPDATING: CartStateKey<'isUpdating'> = 'isUpdating';
const DIRTY: CartStateKey<'dirty'> = 'dirty';
const ACCEPT_TERMS: CartStateKey<'acceptTerms'> = 'acceptTerms';

const generateCartPartId = (cartData: FinnairCart, cartPartId: string) => {
  return `${cartData?.id}-${cartPartId}`;
};

const setCartPartStates = (state: CartState, newStatus: CartPartStatus, partIds: string[] = []) =>
  DataUtils.wrap(state)
    .updateIn(IS_UPDATING, (updatingState: UpdatingCartPart = {}) =>
      partIds.reduce(
        (acc: UpdatingCartPart, cur: string) => setIn(acc, generateCartPartId(state.cartData, cur), newStatus),
        updatingState
      )
    )
    .value();

const all = union({
  clearSelections: SeatMapActions.clearSelections,
  setCartData: CartActions.setCartData,
  setFlights: GlobalBookingActions.setFlights,
  updateFlights: GlobalBookingActions.updateFlight,
  setPaxAmount: GlobalBookingActions.setPaxAmount,
  increasePaxAmountField: GlobalBookingActions.increasePaxAmountField,
  decreasePaxAmountField: GlobalBookingActions.decreasePaxAmountField,
  setTravelType: BookingActions.setTravelType,
  selectTravelClass: BookingActions.selectTravelClass,
  confirmation: GlobalActions.confirmation,
  startLoading: OffersActions.startLoading,
  setInboundId: OffersActions.setInboundId,
  setInboundFareFamily: OffersActions.setInboundFareFamily,
  setOutboundId: OffersActions.setOutboundId,
  setOutboundFareFamily: OffersActions.setOutboundFareFamily,
  setSeatMap: SeatMapActions.setSeatMap,
  setSeatForFlight: SeatMapActions.setSeatForFlight,
  setSelectionsForFragment: ServicesActions.setSelectionsForFragment,
  setSelectionForFragmentForTraveler: ServicesActions.setSelectionForFragmentForTraveler,
  setCoverForId: ServicesActions.setCoverForId,
});

type ExtraActions = typeof all;

// eslint-disable-next-line complexity
export function cartReducer(state = initialState, action: CartActions.ActionsUnion | ExtraActions): CartState {
  switch (action.type) {
    case GlobalBookingActions.setFlights.type:
    case GlobalBookingActions.updateFlight.type:
    case GlobalBookingActions.setPaxAmount.type:
    case GlobalBookingActions.increasePaxAmountField.type:
    case GlobalBookingActions.decreasePaxAmountField.type:
    case BookingActions.setTravelType.type: // @todo Do we need to fix this, as we moved setTravelType from the booking to the globalBooking store?
    case BookingActions.selectTravelClass.type: // @todo Do we need to fix this, as we moved selectTravelClass from the booking to the globalBooking store?
    case OffersActions.setOutboundId.type:
    case OffersActions.setOutboundFareFamily.type:
    case OffersActions.setInboundId.type:
    case OffersActions.setInboundFareFamily.type:
    case CartActions.reset.type:
      return initialState;
    case CartActions.creationStart.type:
      return DataUtils.wrap(state)
        .setIn(CART_STATUS, CartStatus.PENDING_CREATION)
        .deleteIn(CART_URL)
        .deleteIn(CART_DATA)
        .value();

    case CartActions.creationSuccess.type:
      return DataUtils.wrap(state)
        .setIn(CART_STATUS, CartStatus.CREATION_SUCCESS)
        .setIn(CART_URL, action.cartUrl)
        .deleteIn(ACCEPT_TERMS)
        .value();

    case CartActions.creationError.type:
      return setIn(state, CART_STATUS, CartStatus.CREATION_ERROR);

    case CartActions.loadStart.type:
      return setIn(state, CART_STATUS, CartStatus.PENDING);
    case CartActions.loadError.type:
      return setIn(state, CART_STATUS, CartStatus.LOAD_ERROR);

    case CartActions.updateStart.type:
      return setIn(state, CART_STATUS, CartStatus.PENDING);
    case CartActions.updateError.type:
      return setIn(state, CART_STATUS, CartStatus.LOAD_ERROR);
    case CartActions.updateNotCompleted.type:
      return setIn(state, CART_STATUS, CartStatus.OK);

    case CartActions.setCartData.type:
      return DataUtils.wrap(state)
        .setIn(CART_DATA, action.cartData)
        .setIn(CART_STATUS, CartStatus.OK)
        .setIn(DIRTY, false)
        .deleteIn(ACCEPT_TERMS)
        .value();

    case CartActions.isUpdatingPartialCart.type:
      return setCartPartStates(
        state,
        CartPartStatus.UPDATING,
        Array.isArray(action.cartPartIds) ? action.cartPartIds : [action.cartPartIds]
      );
    case CartActions.errorUpdatingPartialCart.type:
      return setCartPartStates(
        state,
        CartPartStatus.ERROR,
        Array.isArray(action.cartPartIds) ? action.cartPartIds : [action.cartPartIds]
      );
    case CartActions.clearUpdatingPartialCart.type:
      return setCartPartStates(
        state,
        CartPartStatus.OK,
        Array.isArray(action.cartPartIds) ? action.cartPartIds : [action.cartPartIds]
      );
    case CartActions.acceptTerms.type:
      return setIn(state, ACCEPT_TERMS, true);

    // Setting services to dirty
    case SeatMapActions.setSeatForFlight.type:
    case SeatMapActions.clearSelections.type:
    case ServicesActions.setSelectionsForFragment.type:
    case ServicesActions.setCoverForId.type:
    case ServicesActions.setSelectionForFragmentForTraveler.type:
      return setIn(state, DIRTY, true);

    case CartActions.fareRulesLoadStart.type:
      return {
        ...state,
        fareRules: {
          ...(state.fareRules || {}),
          [action.travelerId]: {
            ...(state.fareRules?.[action.travelerId] || { hasError: [], ticketRules: {} }),
            isLoading: [...(state.fareRules?.[action.travelerId]?.isLoading || []), action.category],
          },
        },
      };

    case CartActions.fareRulesLoadEnd.type:
      return {
        ...state,
        fareRules: {
          ...state.fareRules,
          [action.travelerId]: {
            ...(state.fareRules?.[action.travelerId] || { hasError: [], ticketRules: {} }),
            isLoading: (state.fareRules?.[action.travelerId]?.isLoading || []).filter((c) => c !== action.category),
          },
        },
      };

    case CartActions.fareRulesError.type:
      return {
        ...state,
        fareRules: {
          ...state.fareRules,
          [action.travelerId]: {
            ...(state.fareRules?.[action.travelerId] || { isLoading: [], ticketRules: {} }),
            hasError: [...(state.fareRules?.[action.travelerId]?.hasError || []), action.category],
          },
        },
      };

    case CartActions.fareRulesCreateCategories.type:
      return {
        ...state,
        fareRules: {
          ...state.fareRules,
          [action.travelerId]: {
            ...(state.fareRules?.[action.travelerId] || { isLoading: [], hasError: [] }),
            ticketRules: {
              ...(state.fareRules?.[action.travelerId]?.ticketRules || {}),
              ...action.rules,
            },
          },
        },
      };

    case CartActions.fareRulesClear.type:
      return {
        ...state,
        fareRules: {},
      };

    // Reset cart when back to offer
    case OffersActions.startLoading.type:
      return initialState;
    default:
      return state;
  }
}
